/**
 * Boot-time session hint.
 *
 * On startup the Firebase SDK validates the persisted session with an
 * `accounts:lookup` round-trip before it exposes `currentUser` — so the
 * entire boot request burst serializes behind a Google round-trip (~0.5s on
 * real networks). The SDK persists the ID token (a self-contained JWT the
 * backend verifies on every request anyway) and the account email in its
 * own IndexedDB store. Reading them directly lets the authentication store
 * optimistically flip to SIGNED_IN and the boot burst fire immediately, in
 * parallel with the SDK's validation; requests that lose the race get a
 * one-shot retry with the settled token (see the 401 handling in
 * `lib/openapi/client.ts`).
 *
 * Returns null whenever anything is off (no DB yet, signed out, token
 * within the expiry margin) — callers then fall back to waiting for the
 * SDK as before. Sign-out clears the SDK's IndexedDB entry, so a
 * signed-out browser never produces a hint.
 */

// Names are part of firebase-js-sdk's persistence contract
// (packages/auth/src/platform_browser/persistence/indexed_db.ts).
const DB_NAME = "firebaseLocalStorageDb";
const STORE_NAME = "firebaseLocalStorage";

// Don't serve tokens about to expire — the SDK would refresh them, and a
// request still in flight at actual expiry would 401.
const EXPIRY_MARGIN_MS = 60_000;

export interface PersistedSession {
    token: string;
    email: string | null;
}

let readOnce: Promise<PersistedSession | null> | undefined;

/**
 * The Firebase-persisted session (ID token + email) for the default app,
 * or null. Memoized: the hint is only meant for the boot window, so one
 * read serves all callers.
 */
export const readPersistedSession = (
    apiKey: string
): Promise<PersistedSession | null> => {
    if (!readOnce) {
        readOnce = readSession(apiKey).catch(() => null);
    }
    return readOnce;
};

const readSession = async (
    apiKey: string
): Promise<PersistedSession | null> => {
    if (typeof indexedDB === "undefined") {
        return null;
    }

    const db = await new Promise<IDBDatabase | null>((resolve) => {
        const request = indexedDB.open(DB_NAME);
        request.onsuccess = () => resolve(request.result);
        request.onerror = () => resolve(null);
        request.onblocked = () => resolve(null);
    });
    if (!db) {
        return null;
    }

    try {
        if (!db.objectStoreNames.contains(STORE_NAME)) {
            return null;
        }
        const entry = await new Promise<unknown>((resolve) => {
            const request = db
                .transaction(STORE_NAME, "readonly")
                .objectStore(STORE_NAME)
                .get(`firebase:authUser:${apiKey}:[DEFAULT]`);
            request.onsuccess = () => resolve(request.result);
            request.onerror = () => resolve(null);
        });

        const user = (
            entry as
                | {
                      value?: {
                          email?: string;
                          stsTokenManager?: {
                              accessToken?: string;
                              expirationTime?: number;
                          };
                      };
                  }
                | undefined
        )?.value;
        const { accessToken, expirationTime } = user?.stsTokenManager ?? {};
        if (!accessToken || !expirationTime) {
            return null;
        }
        if (Date.now() > expirationTime - EXPIRY_MARGIN_MS) {
            return null;
        }
        return { token: accessToken, email: user?.email ?? null };
    } finally {
        db.close();
    }
};
